import React from "react";
import PropTypes from "prop-types";
// Material imports
import Link from "@mui/material/Link";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import makeStyles from "@mui/styles/makeStyles";

//Limitless imports
import { ExpertTaskList, ExpertTaskFilters, useExpertProfileContext, useMessagePass } from "@limitless/expert";
import { useTranslation } from "@limitless/common/hooks";
import { modernTheme } from "@limitless/themeing/modern-theme/ModernTheme";
//Modern imports
import Typography from "@limitless/modern/data-display/typography/Typography";
import Grid from "@limitless/modern/layout/Grid";
import Refresh from "@limitless/icons/Refresh";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import ModernTaskListItem from "./modern-task-list-item/ModernTaskListItem";
import noMessage from "../../assets/no-message.gif";
import { useLocation } from "react-router-dom";
import { useExpertTaskListContext, useTaskListFilter, useExpertMessageContext } from "@limitless/expert";
import { useHistory } from "react-router-dom";
const ModernTaskList = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { tasklist, tasklistFilters, fetchTaskList, fetchPassedTaskList } = useExpertTaskListContext();
    const [hasClaim, setHasClaim] = React.useState(false);
    const { passMessage } = useMessagePass();
    const { clearMessage, onClaimMessage } = useExpertMessageContext();
    const { setPassedTaskFilter } = useTaskListFilter();

    const { isDarkMode } = useExpertProfileContext();
    const location = useLocation();
    const history = useHistory();

    const theme = React.useMemo(() => modernTheme(isDarkMode ? "dark" : "light"), [isDarkMode]);

    const [tabId, setTabId] = React.useState(0);
    const [totalMessageCount, setTotalMessageCount] = React.useState(tasklist.count?.total ?? 0);

    const messageLength = React.useMemo(() => {
        return Object.keys(tasklist.filteredMessages)?.length;
    }, [Object.keys(tasklist.filteredMessages), tasklistFilters.selectedFilters]);

    React.useEffect(() => {
        const count = messageLength - tasklist.count.count;
        const countValue =
            count > 0
                ? tasklist.count?.total + count
                : count === 0
                ? tasklist.count?.total
                : tasklist.count?.total - -count;
        setTotalMessageCount(countValue ?? tasklist.count?.total);
    }, [messageLength, tasklist.count?.total, totalMessageCount]);

    React.useEffect(() => {
        if (tabId === 0) {
            tasklistFilters.passedTasks.pass.value = false;
        } else {
            tasklistFilters.passedTasks.pass.value = true;
        }
    }, []);

    const handleChangeTab = (e, newId) => {
        setTabId(newId);
        if (newId === 0) {
            tasklistFilters.passedTasks.pass.value = false;
            setPassedTaskFilter(false);
            fetchTaskList();
        } else {
            tasklistFilters.passedTasks.pass.value = true;
            setPassedTaskFilter(true);
            fetchPassedTaskList();
        }
    };

    const handlePassMessage = message => {
        passMessage(true);
        clearMessage();
    };

    return (
        <ExpertTaskList>
            {({
                loading,
                hasMessages,
                newMessageLoading,
                selectedMessageId,
                removeTasklistRecord,
                onSelectMessage,
                claimSelectedMessage,
                onTaskListRefresh,
                messages,
                onUnselectMessage
            }) => {
                React.useEffect(() => {
                    const task = location?.state?.task;
                    if (task) {
                        claimSelectedMessage(task, (messageResources, profile, messageInbox) => {
                            onClaimMessage(messageInbox, messageResources, profile, data => {
                                if (data.hasError) {
                                    setHasClaim(false);
                                } else {
                                    history.replace({
                                        ...location,
                                        state: {}
                                    });
                                }
                            });
                        });
                    }
                }, [location]);

                const renderedTaskListItems = React.useMemo(() => {
                    return Object.keys(messages).map(messageKey => {
                        const message = messages[messageKey];

                        return (
                            <div key={message.messageId}>
                                <ModernTaskListItem
                                    message={message}
                                    selected={selectedMessageId === message.messageId}
                                    handlePassMessage={() => handlePassMessage(message)}
                                    onRemoveTask={removeTasklistRecord}
                                    setHasClaim={setHasClaim}
                                    showPassButton={true}
                                    tabId={tabId}
                                    onSelectMessage={onSelectMessage}
                                    claimSelectedMessage={claimSelectedMessage}
                                    loading={newMessageLoading && selectedMessageId === message.messageId}
                                    selectedMessageId={selectedMessageId}
                                    onUnselectMessage={onUnselectMessage}
                                    onTaskListRefresh={onTaskListRefresh}
                                />
                            </div>
                        );
                    });
                }, [messages, selectedMessageId, newMessageLoading, tabId]);

                return (
                    <>
                        <Grid
                            container
                            justifyContent={"space-between"}
                            pl={"12px"}
                            pr={2}
                            sx={{
                                borderRadius: "16px 16px 0px 0px",
                                boxShadow: "0px 5px 25px -5px rgba(0, 0, 0, 0.08)",
                                background: theme.palette.background.elevation1
                            }}
                        >
                            <Grid display="flex" justifyContent={"space-between"} flexDirection="row">
                                <IconButton
                                    icon={<Refresh />}
                                    label=""
                                    disabled={hasClaim}
                                    onClick={onTaskListRefresh}
                                    id="refreshBtn"
                                />
                                <Tabs
                                    value={tabId}
                                    onChange={handleChangeTab}
                                    indicatorColor="primary"
                                    textColor="primary"
                                >
                                    <Tab
                                        label={
                                            <Tooltip
                                                arrow
                                                title="View all available tasks for response or review."
                                                classes={{
                                                    popper: classes.tooltipWrapper,
                                                    tooltip: classes.tooltip
                                                }}
                                                enterDelay={500}
                                            >
                                                <span>
                                                    <Typography variant="subtitle1" sx={{ textTransform: "none" }}>
                                                        All Tasks
                                                    </Typography>
                                                </span>
                                            </Tooltip>
                                        }
                                        style={{ marginLeft: 16 }}
                                    />
                                    <Tab
                                        label={
                                            <Tooltip
                                                arrow
                                                placement="bottom"
                                                classes={{
                                                    popper: classes.tooltipWrapper,
                                                    tooltip: classes.tooltip
                                                }}
                                                enterDelay={500}
                                                title="View previously passed tasks still available for response or review."
                                            >
                                                <span>
                                                    <Typography variant="subtitle1" sx={{ textTransform: "none" }}>
                                                        Passed Tasks
                                                    </Typography>
                                                </span>
                                            </Tooltip>
                                        }
                                    />
                                </Tabs>
                            </Grid>
                            {(tasklist.count?.total || !isNaN(totalMessageCount)) && (
                                <Typography variant="caption" display="flex" alignItems="center">
                                    {t("Displaying first {0}", Object.keys(messages)?.length ?? 0)}{" "}
                                    {t(
                                        "of {0} available tasks, sorted by time remaining",
                                        isNaN(totalMessageCount) ? tasklist.count?.total ?? 0 : totalMessageCount
                                    )}
                                </Typography>
                            )}
                        </Grid>

                        {loading && (
                            <Grid container sx={{ height: "calc(100vh - 155px)" }}>
                                <Typography
                                    color="textPrimary"
                                    id="fetchingTasks"
                                    pl={"12px"}
                                    pr={2}
                                    pb={"12px"}
                                    pt={"12px"}
                                >
                                    {"Fetching tasks..."}
                                </Typography>
                            </Grid>
                        )}
                        {!loading && !hasMessages && (
                            <ExpertTaskFilters>
                                {({ onClearFilters }) => (
                                    <Grid
                                        container
                                        alignItems="center"
                                        justifyContent="center"
                                        direction="column"
                                        sx={{ height: "calc(100vh - 155px)", marginTop: -3 }}
                                    >
                                        <img src={noMessage} />
                                        <Grid
                                            sx={{ position: "absolute", top: window.innerHeight / 1.3 }}
                                            container
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Typography color="textPrimary" variant="subtitle1">
                                                {"No tasks available"}
                                            </Typography>
                                            <Grid container alignItems="center" justifyContent="center">
                                                <Typography color="textPrimary" variant="body2">
                                                    You can
                                                </Typography>
                                                <Typography
                                                    color="primary"
                                                    variant="subtitle2"
                                                    ml={0.5}
                                                    style={{ cursor: "pointer", textDecoration: "none" }}
                                                    component={Link}
                                                    onClick={onClearFilters}
                                                    id="clearFilterTextBtn"
                                                >
                                                    clear filters
                                                </Typography>
                                                <Typography color="textPrimary" variant="body2" ml={0.5}>
                                                    or
                                                </Typography>
                                                <Typography
                                                    color="primary"
                                                    variant="subtitle2"
                                                    style={{ cursor: "pointer", textDecoration: "none" }}
                                                    onClick={onTaskListRefresh}
                                                    ml={0.5}
                                                    component={Link}
                                                    id="refreshTextBtn"
                                                >
                                                    refresh
                                                </Typography>
                                                <Typography color="textPrimary" variant="body2" ml={0.5}>
                                                    your inbox
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                )}
                            </ExpertTaskFilters>
                        )}
                        {!loading && hasMessages && (
                            <Grid
                                sx={{
                                    overflow: "auto",
                                    scrollbarWidth: "none",
                                    whiteSpace: "nowrap",
                                    height: "calc(100vh - 155px)"
                                }}
                            >
                                {renderedTaskListItems}
                            </Grid>
                        )}
                    </>
                );
            }}
        </ExpertTaskList>
    );
};

const useStyles = makeStyles(() => ({
    tooltipWrapper: {
        maxWidth: 220
    },
    tooltip: {
        textAlign: "center"
    }
}));

export default ModernTaskList;

ModernTaskList.propTypes = {
    conversationError: PropTypes.bool,
    onSelectMessage: PropTypes.func
};
